import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';
import { adaptLegacyWorkspaceRoutingForLinks } from './adaptLegacyWorkspaceRoutingForLinks';

/**
 * Layer 3 — entity behavior: detail links, public URLs, slug prefixes.
 *
 * Read `enterprise_workspace_id` (and `team_id` where applicable) from the entity record.
 * Do not use browsing scope or the `enterprise` feature flag here.
 *
 * @see .cursor/rules/workspace-scoping-layers.mdc
 */

export type WorkspaceRef = { id: number; slug: string };

export type TeamRef = { id: number; slug: string };

export type WorkspaceScopedEntity = {
  enterprise_workspace_id?: number | null;
};

export type TeamScopedEntity = WorkspaceScopedEntity & {
  enterprise_team_id?: number | null;
};

export function getSharedEnterpriseWorkspaceId(
  entities: WorkspaceScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const workspaceIds = entities.map(
    entity => entity.enterprise_workspace_id ?? null
  );
  const definedIds = [
    ...new Set(workspaceIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedWorkspaceId = definedIds[0]!;
  if (workspaceIds.some(id => id != null && id !== sharedWorkspaceId)) {
    return null;
  }

  if (workspaceIds.some(id => id === null)) {
    return null;
  }

  return sharedWorkspaceId;
}

export function resolveEntityWorkspaceSlug(
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
): string | undefined {
  if (entity.enterprise_workspace_id == null || !workspaces?.length) {
    return undefined;
  }

  return workspaces.find(
    workspace => workspace.id === entity.enterprise_workspace_id
  )?.slug;
}

export function buildEntityWorkspaceRoutes(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
) {
  const workspaceSlug = resolveEntityWorkspaceSlug(entity, workspaces);

  if (workspaceSlug) {
    return appRouting.org(organizationSlug).workspace(workspaceSlug);
  }

  return adaptLegacyWorkspaceRoutingForLinks(
    myRouting.forWorkspace(organizationSlug)
  );
}

export function getContactDetailPath(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined,
  contactId: string | number
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    entity,
    workspaces
  ).contacts.detail(contactId);
}

export function getCalloutDetailPath(
  organizationSlug: string,
  callout: WorkspaceScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    callout,
    workspaces
  ).callouts.detail(callout.id);
}

export function resolveEntityTeamSlug(
  entity: TeamScopedEntity,
  teams: TeamRef[] | undefined
): string | undefined {
  if (entity.enterprise_team_id == null || !teams?.length) {
    return undefined;
  }

  return teams.find(team => team.id === entity.enterprise_team_id)?.slug;
}

/**
 * Detail path for a production: team URL when team id resolves, else legacy.
 * No workspace-without-team home (same as callsheets).
 */
export function getProductionDetailPath(
  organizationSlug: string,
  production: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams?: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(production, workspaces);
  const teamSlug = resolveEntityTeamSlug(production, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .productions.detail(production.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .productions()
    .detail(production.id);
}

export function getSharedEnterpriseTeamId(
  entities: TeamScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const teamIds = entities.map(entity => entity.enterprise_team_id ?? null);
  const definedIds = [
    ...new Set(teamIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedTeamId = definedIds[0]!;
  if (teamIds.some(id => id != null && id !== sharedTeamId)) {
    return null;
  }

  if (teamIds.some(id => id === null)) {
    return null;
  }

  return sharedTeamId;
}
